import { Color, LinkedListContainer } from "pixi-particles";
import { Texture, Container } from "pixi.js";
import { TrailEmitterConfig, TrailPath } from "./TrailPath";
import TrailPathConfig from "./TrailPathConfig";

/**
 * Class intended for particle trail paths reuse.
 *
 * @export
 * @class TrailPool
 */
export class TrailPool {
    /**
     * Gets the trail pool pixi container, used to add trail particle effects to.
     *
     * @type {Container}
     * @memberof TrailPool
     */
    public readonly container: Container;

    private inactiveTrails: TrailPath[];
    private activeTrais: TrailPath[];
    private isDestroyed: boolean;

    /**
     * Creates an instance of TrailPool.
     * @memberof TrailPool
     */
    constructor() {
        // Using the currently fastest for particles container.
        this.container = new LinkedListContainer();

        // Init the fields
        this.inactiveTrails = new Array<TrailPath>();
        this.activeTrais = new Array<TrailPath>();
        this.isDestroyed = false;
    }

    /**
     * Checks whether the pool is currently active or not.
     *
     * @readonly
     * @type {boolean}
     * @memberof TrailPool
     */
    public get isActive(): boolean {
        return this.activeTrais.length > 0;
    }

    /**
     * Gets a new or existing trail from the pool.
     *
     * @param {Color} color The color to apply to the trail returned.
     * @param {number} quality The trail quality in range 0.1 - 1.0.
     * Higher number means better quality, but please note many trails
     * at the scene (usually 100+) causes performance issues. 
     * @return {*}  {TrailPath} New or existing, non active trail.
     * @memberof TrailPool
     */
    public getTrail(color: Color, quality: number = 1.0, texture: Texture = undefined, emitterConfig: TrailEmitterConfig = undefined, trailConfig: TrailPathConfig = undefined): TrailPath {
        let target = this.inactiveTrails.find(t => !t.destroyed);

        this.container.visible = true;
        if (target) {
            this.inactiveTrails.splice(this.inactiveTrails.indexOf(target), 1);
            target.color = color;
            target.quality = quality;
            target.config = emitterConfig || new TrailEmitterConfig(color, quality);
            
            this.activeTrais.push(target);
            return target;
        } else {
            if (!emitterConfig) {
                emitterConfig = new TrailEmitterConfig(color, quality);
            }
            
            let trail = new TrailPath(this.container, emitterConfig, () => { this.onTrailCompleted(trail); }, texture, trailConfig);
            trail.color = color;
            trail.quality = quality;
            this.activeTrais.push(trail);
            return trail;
        }
    }

    /**
     * Stops all the active trails.
     *
     * @return {*}  {void}
     * @memberof TrailPool
     */
    public stopAll(): void {
        if (this.isDestroyed) {
            return;
        }

        for (let index = 0; index < this.activeTrais.length; index++) {
            const activeTrail = this.activeTrais[index];
            activeTrail.stop(false);
            this.inactiveTrails.push(activeTrail);
        }

        this.activeTrais.splice(0, this.activeTrais.length);
        this.container.visible = false;
    }

    /**
     * Stops and destroys all the trails.
     *
     * @memberof TrailPool
     */
    public destroyAll(): void {
        this.stopAll();
        for (let index = 0; index < this.inactiveTrails.length; index++) {
            const trail = this.inactiveTrails[index];
            trail.destroy();
        }

        this.inactiveTrails.splice(0, this.inactiveTrails.length);
        this.container.removeChildren();
    }

    private onTrailCompleted(trail: TrailPath) {
        if (this.isDestroyed) {
            return;
        }

        this.activeTrais.splice(this.activeTrais.indexOf(trail), 1);
        this.inactiveTrails.push(trail);
    }
}